import { useEffect, useMemo, useRef, useState } from 'react';
import type { ComponentContentSchema, SlotGroupDefinition } from '@vibesites/runtime';
import { resolveRichText } from '@vibesites/runtime';
import { useSlotGroup, usePropertyField, useContentToken, useVibesites } from '@vibesites/runtime/react';
import { RichTextContent } from '@vibesites/runtime/react';
import { EditableToken } from '@vibesites/runtime/react';
import { EditableSlotGroup } from '@vibesites/runtime/react';
import {
  VibesitesImage,
  getMediaUrl,
  parseStorageKey,
  videoThumbStorageKey,
} from '@vibesites/ui-primitives';

/**
 * Title-case a standalone "studio" wherever it appears in the *text* of an
 * HTML string, leaving tags and attributes untouched. The subtitle's studio
 * casing must be guaranteed at render time (not just in the generated default)
 * because a property may carry a saved `hero.subtitle` override — a snapshot
 * baked before the range formatter was fixed — and that stored value wins over
 * the generated default in the runtime's token resolution. Normalizing the
 * final resolved HTML catches both the default and any stale override.
 *
 * The regex matches each text run (from string start or a `>` up to the next
 * `<`) so replacements never reach inside `< … >` (tag names / class lists like
 * "md:hidden"), only visible copy.
 */
function titleCaseStudio(html: string): string {
  return html.replace(/(^|>)([^<]*)/g, (_m, lead: string, text: string) =>
    lead + text.replace(/\bstudio\b/gi, 'Studio'),
  );
}

/**
 * Renders the hero subtitle token with a hard guarantee that any "studio" in
 * the bedrooms range shows as "Studio". Mirrors <RichTextContent as="p">:
 * resolves the token (override → default), resolves embedded field tokens, then
 * unwraps a single outer <p> so the HTML nests validly inside the <p> wrapper
 * EditableToken renders (a <p> inside a <p> breaks SSR hydration). Content with
 * real block-level structure falls back to a <div> wrapper, exactly as
 * RichTextContent does. The extra step over RichTextContent is titleCaseStudio,
 * applied to the resolved HTML regardless of whether it came from the generated
 * default or a saved override.
 */
function HeroSubtitle({ defaultValue, className }: { defaultValue: string; className?: string }) {
  const { property } = useVibesites();
  const raw = useContentToken('hero.subtitle', { defaultValue }) || '';
  const resolved = titleCaseStudio(resolveRichText(raw, property as Record<string, any>));

  const single = resolved.match(/^\s*<p>([\s\S]*)<\/p>\s*$/);
  const inner =
    single && !/<(?:p|div|h[1-6]|ul|ol|li|blockquote)[\s>]/i.test(single[1]) ? single[1] : null;
  const tag = inner ? 'p' : 'div';
  const htmlToRender = inner ?? resolved;

  const subtitleStyle = {
    fontFamily: '"Poppins", sans-serif',
    fontWeight: 600 as const,
    // Mobile letter-spacing is tightened in .hero-subtitle CSS so long
    // uppercase lines don't clip against the section's overflow-hidden.
    letterSpacing: '0.05em',
    textTransform: 'uppercase' as const,
  };

  return (
    <EditableToken
      path="hero.subtitle"
      type="richtext"
      label="Subtitle"
      defaultValue={defaultValue}
      as={tag}
      className={className}
      style={subtitleStyle}
    >
      <span
        data-vibesite-richtext=""
        dangerouslySetInnerHTML={{ __html: htmlToRender }}
        style={{ display: 'contents', ...subtitleStyle }}
      />
    </EditableToken>
  );
}

/**
 * Formats a raw bedrooms range (e.g. "studio - 2") into natural copy
 * like "Studio - 2 Bedroom". Handles single values ("1" → "1 Bedroom"),
 * ranges ("1 - 3" → "1 - 3 Bedroom"), and studios. Equal endpoints
 * collapse to a single value (e.g. "2 - 2" → "2 Bedroom",
 * "Studio - Studio" → "Studio").
 */
function formatBedroomsLabel(raw: string | null | undefined): string {
  if (!raw) return 'Apartments';
  const trimmed = raw.trim();
  if (!trimmed) return 'Apartments';

  const parts = trimmed.split(/\s*-\s*/);
  const formatted = parts.map((p) => {
    const trimmedPart = p.trim();
    if (trimmedPart.toLowerCase() === 'std') return 'Studio';
    // Title-case the word "studio" wherever it appears in the segment
    // (whole-word, case-insensitive) so any range containing a studio —
    // "studio - 2", "studio", "STUDIO" — always renders "Studio".
    return trimmedPart.replace(/\bstudio\b/gi, 'Studio');
  });

  // Collapse equal endpoints: "2 - 2" → "2", "Studio - Studio" → "Studio".
  // Compare case-insensitively so "studio - Studio" still collapses cleanly.
  const deduped =
    formatted.length > 1 && formatted.every((p) => p.toLowerCase() === formatted[0].toLowerCase())
      ? [formatted[0]]
      : formatted;

  const label = deduped.join(' - ');
  const allStudios = deduped.every((p) => p === 'Studio');
  return allStudios ? `${label}` : `${label} Bedroom`;
}

/**
 * One tileable band of parallel waves. Geometry is driven entirely by props so
 * we can render a full-size band on desktop and a compressed one on mobile
 * (half the vertical `spacing` between lines and half the band `height`) without
 * touching the desktop look. Each instance needs a unique `patternId`, and
 * `className` toggles per-breakpoint visibility.
 *
 * `yStart` is the y-position of the FIRST wave baseline. Each wave bulges up by
 * `amp / 2`, so with `yStart = 0` the top wave's crest bleeds above the band and
 * clips away (the desktop look). Setting `yStart` to about `amp / 2` drops the
 * first baseline down so that top crest lands inside the band — a full extra
 * wave becomes visible in what would otherwise be an empty trough at the top.
 */
function HeroWaveBand({
  patternId,
  height,
  spacing,
  amp = 25,
  yStart = 0,
  className = '',
}: {
  patternId: string;
  height: number;
  spacing: number;
  amp?: number;
  yStart?: number;
  className?: string;
}) {
  return (
    <svg
      className={`w-full ${className}`}
      width="100%"
      height={height}
      xmlns="http://www.w3.org/2000/svg"
    >
      <defs>
        <pattern id={patternId} x="0" y="0" width="280" height={height} patternUnits="userSpaceOnUse">
          {Array.from({ length: Math.ceil((height - yStart) / spacing) + 2 }).map((_, i) => {
            const y = yStart + i * spacing;
            return (
              <path
                key={i}
                d={`M0 ${y} q70 ${-amp} 140 0 t140 0`}
                fill="none"
                stroke="var(--vibe-color-secondary)"
                strokeWidth={1.5}
              />
            );
          })}
        </pattern>
      </defs>
      <rect width="100%" height={height} fill={`url(#${patternId})`} />
    </svg>
  );
}

const featuredImageDef: SlotGroupDefinition = {
  label: 'Background Media',
  description: 'If a video is set, it plays as the background. Otherwise the image is used.',
  fields: {
    image: {
      label: 'Image',
      type: 'image',
      defaultField: 'property_featured_image',
    },
    video: {
      label: 'Video',
      type: 'video',
      defaultField: 'property_featured_video',
    },
    alt: {
      label: 'Alt Text',
      type: 'text',
      defaultValue: 'Property hero image',
    },
  },
};

const ctaLinkDef: SlotGroupDefinition = {
  label: 'CTA Link',
  fields: {
    url: {
      label: 'URL',
      type: 'url',
      defaultValue: '/floor-plans',
    },
    label: {
      label: 'Link Text',
      type: 'text',
      defaultValue: 'View Availability',
    },
    _target: {
      label: 'Target',
      type: 'text',
      defaultValue: '_self',
    },
  },
};

export function Hero() {
  const bgImage = useSlotGroup('hero.bg_image', featuredImageDef);
  const ctaLink = useSlotGroup('hero.cta_link', ctaLinkDef);

  const bedroomsRange = usePropertyField('property_bedrooms_range');
  const city = usePropertyField('property_address_city');
  const state = usePropertyField('property_address_state');

  const showVideo = !!bgImage.video;
  // Poster from the bound video thumb so overrides stay correct; fall back to image.
  const thumbKey = videoThumbStorageKey(parseStorageKey(bgImage.video || null));
  const poster =
    (thumbKey ? getMediaUrl(thumbKey, { width: 1920 }) : null) || bgImage.image || '';

  // Respect prefers-reduced-motion: drop autoplay (pause() alone loses the race
  // if the video hasn't buffered yet) and pause anything already playing.
  const videoRef = useRef<HTMLVideoElement>(null);
  const [reducedMotion, setReducedMotion] = useState(false);
  useEffect(() => {
    const mq = window.matchMedia('(prefers-reduced-motion: reduce)');
    const apply = () => {
      setReducedMotion(mq.matches);
      if (mq.matches) videoRef.current?.pause();
    };
    apply();
    mq.addEventListener('change', apply);
    return () => mq.removeEventListener('change', apply);
  }, []);

  const subtitleHtml = useMemo(() => {
    const beds = formatBedroomsLabel(bedroomsRange as string | null);
    const cityStr = city || '';
    const stateStr = state || '';
    const location = [cityStr, stateStr].filter(Boolean).join(', ');
    // Line-break behavior differs by breakpoint:
    //  - Mobile (< md): break before "Rent" so the whole "Rent in <city>, <state>"
    //    phrase sits together on the 2nd line (via `<br class="md:hidden">`).
    //  - Desktop (≥ md): keep "... Apartments for Rent" on line 1 and break
    //    before "in <city>, <state>" (via `<br class="max-md:hidden">`).
    // Spaces around each <br> are intentional so the words stay separated when
    // the corresponding break is hidden (display:none adds no whitespace).
    return `<p>${beds} Apartments for ${location ? `<br class="md:hidden"/>Rent <br class="max-md:hidden"/>in ${location}` : 'Rent'}</p>`;
  }, [bedroomsRange, city, state]);

  return (
    <>
    <section className="relative w-full h-[calc(70vh-50px)] md:h-[92vh] overflow-hidden">
      {/* Background media: featured video when set, otherwise featured image */}
      <EditableSlotGroup groupPath="hero.bg_image" definition={featuredImageDef}>
        <div className="absolute inset-0">
          {showVideo ? (
            /* Decorative: the overlaid headline carries the content. */
            <video
              ref={videoRef}
              className="w-full h-full object-cover"
              src={bgImage.video || undefined}
              poster={poster}
              autoPlay={!reducedMotion}
              muted
              loop
              playsInline
              aria-hidden
            />
          ) : bgImage.image ? (
            <VibesitesImage
              url={bgImage.image}
              alt={bgImage.alt || ''}
              priority
              sizes="100vw"
              className="w-full h-full object-cover"
            />
          ) : (
            <div className="w-full h-full bg-[var(--vibe-color-text)]" />
          )}
        </div>
      </EditableSlotGroup>

      {/* Gradient overlay for text contrast — purely decorative, must not
          intercept clicks (otherwise floating chatbot iframes / widgets
          rendered above the hero can't be clicked through the empty
          gradient area). */}
      <div className="absolute inset-0 bg-gradient-to-t from-black/70 via-black/35 to-black/20 pointer-events-none" />

      {/* Content. Padded equally above the subtitle and below the CTA within
          the visible hero (below the fixed header + offer banner). Pointer
          events stay off on the wrapper and on only on interactive children
          so empty hero space passes clicks through (e.g. RentCafe chatbot). */}
      <div className="hero-content relative z-10 flex flex-col items-center justify-center h-full text-center text-white px-10 sm:px-12 md:px-8 pointer-events-none">
        <HeroSubtitle
          defaultValue={subtitleHtml}
          className="hero-subtitle hero-entrance hero-entrance-1 text-sm sm:text-lg md:text-2xl lg:text-[2.125rem] uppercase w-full max-w-4xl mb-4 px-1 leading-snug text-balance break-words drop-shadow-md pointer-events-auto"
        />

        <RichTextContent
          path="hero.title"
          label="Title"
          defaultValue='<p><span data-type="field-token" data-field="property_name"></span></p>'
          as="h1"
          className="hero-entrance hero-entrance-2 hero-title-glow font-heading text-[clamp(1.375rem,calc(0.6rem+5.2vw),3.75rem)] sm:text-4xl md:text-5xl lg:text-6xl font-bold mb-6 w-full max-w-6xl leading-[1.05] tracking-tight text-balance drop-shadow-lg uppercase pointer-events-auto"
        />

        <EditableSlotGroup groupPath="hero.cta_link" definition={ctaLinkDef}>
          <a
            href={ctaLink.url || '/floor-plans'}
            target={ctaLink._target || '_self'}
            className="hero-entrance hero-entrance-3 hero-cta group/cta relative inline-flex items-center gap-2 whitespace-nowrap px-6 py-3 font-heading text-sm md:px-4 md:tracking-[0.1em] lg:px-8 lg:py-3.5 lg:text-base lg:tracking-[0.15em] font-normal tracking-[0.15em] uppercase text-white overflow-hidden transition-all duration-300 pointer-events-auto"
          >
            <span className="relative z-10">{ctaLink.label || 'View Availability'}</span>
          </a>
        </EditableSlotGroup>
      </div>

      <style>{`
        @keyframes heroEntrance {
          from { opacity: 0; transform: translateY(20px); }
          to   { opacity: 1; transform: translateY(0); }
        }
        .hero-entrance {
          animation: heroEntrance 0.8s cubic-bezier(0.22, 1, 0.36, 1) both;
        }
        /* Clear the fixed header, then apply the SAME inset above the subtitle
           and below the CTA so the content block is vertically balanced in the
           visible hero. --offer-banner-height is set by SpecialOfferBanner.
           Nav clearance: 84px mobile / 100px md / 92px lg. */
        .hero-content {
          --hero-nav-clearance: calc(var(--offer-banner-height, 0px) + 5.25rem);
          --hero-y-inset: 2.25rem;
          padding-top: calc(var(--hero-nav-clearance) + var(--hero-y-inset));
          padding-bottom: var(--hero-y-inset);
          transition: padding-top 0.5s cubic-bezier(0.4, 0, 0.2, 1),
            padding-bottom 0.5s cubic-bezier(0.4, 0, 0.2, 1);
        }
        @media (min-width: 768px) {
          .hero-content {
            --hero-nav-clearance: calc(var(--offer-banner-height, 0px) + 6.25rem);
            --hero-y-inset: 1.75rem;
          }
        }
        @media (min-width: 1024px) {
          .hero-content {
            --hero-nav-clearance: calc(var(--offer-banner-height, 0px) + 5.75rem);
            --hero-y-inset: 1.5rem;
          }
        }
        /* Hard-lock subtitle type: Poppins SemiBold, all caps.
           Do NOT use theme font tokens — property/theme font overrides must not win.
           Tracking is tighter on small screens so long one-line overrides
           (e.g. "TOWNHOMES FOR RENT IN …") don't clip under overflow-hidden. */
        .hero-subtitle,
        .hero-subtitle *,
        .hero-subtitle [data-vibesite-richtext],
        .hero-subtitle [data-vibesite-richtext] * {
          font-family: "Poppins", sans-serif !important;
          font-weight: 600 !important;
          font-style: normal !important;
          letter-spacing: 0.05em !important;
          text-transform: uppercase !important;
        }
        @media (min-width: 768px) {
          .hero-subtitle,
          .hero-subtitle *,
          .hero-subtitle [data-vibesite-richtext],
          .hero-subtitle [data-vibesite-richtext] * {
            letter-spacing: 0.075em !important;
          }
        }
        .hero-entrance-1 { animation-delay: 0.1s; }
        .hero-entrance-2 { animation-delay: 0.35s; }
        .hero-entrance-3 { animation-delay: 0.6s; }
        /* Filled pill matching the reference site's hero CTA: solid primary
           fill, fully-rounded ends, white (on-primary) label, no border.
           Mirrors the header "Contact Us" pill so the two primary buttons
           share one visual language. Darkens slightly and lifts on hover. */
        .hero-cta {
          background: var(--vibe-color-primary);
          border: none;
          border-radius: 9999px;
          color: var(--vibe-color-text-on-primary);
          box-shadow: none;
        }
        .hero-cta:hover {
          background: color-mix(in srgb, var(--vibe-color-primary), black 12%);
          color: var(--vibe-color-text-on-primary);
          transform: translateY(-2px);
          box-shadow: 0 6px 20px color-mix(in srgb, var(--vibe-color-primary) 30%, transparent);
        }
        .hero-cta:active {
          transform: translateY(0);
          box-shadow: none;
        }
        .hero-title-glow {
          text-shadow:
            0 0 12px rgba(0, 0, 0, 0.35),
            0 0 30px rgba(0, 0, 0, 0.18);
        }
        @media (prefers-reduced-motion: reduce) {
          .hero-entrance { animation: none; opacity: 1; transform: none; }
        }
      `}</style>
    </section>

    {/* Wave graphic band below the hero — a repeating band of parallel wavy
        lines in the theme's secondary color, echoing the reference site's wave
        pattern. Decorative only (aria-hidden), sits between the hero image and
        the next section. The pattern uses userSpaceOnUse units so it tiles
        seamlessly across any viewport width without distorting the wave. */}
    <div className="w-full bg-[var(--vibe-color-surface)] leading-[0]" aria-hidden="true">
      {/* Desktop band (≥ lg): spacing 15, height 100. Waves tile seamlessly
          across any width; top crests bleed up into the hero and bottom lines
          break at the edge. Hidden below lg in favor of the compressed variant. */}
      <HeroWaveBand
        patternId="heroWavePattern"
        height={100}
        spacing={15}
        className="hidden lg:block"
      />
      {/* Mobile + tablet band (< lg): spacing, height AND amplitude all halved
          (7.5 / 50 / 12.5) so the band is a true 2× reduction of the desktop
          pattern. Halving the amplitude too keeps the crest depth (amp/2) below
          the line spacing, so the lines stay distinct and pack evenly right up to
          the top edge instead of overlapping into gaps. */}
      <HeroWaveBand
        patternId="heroWavePatternMobile"
        height={50}
        spacing={7.5}
        amp={12.5}
        className="block lg:hidden"
      />
    </div>
    </>
  );
}

export const contentSchema: ComponentContentSchema = {
  slot: 'Hero',
  displayName: 'Hero Section',
  tokens: {
    'hero.title': {
      label: 'Title',
      type: 'richtext',
      defaultValue:
        '<p><span data-type="field-token" data-field="property_name"></span></p>',
    },
    'hero.subtitle': {
      label: 'Subtitle',
      type: 'richtext',
      defaultValue: '<p>Apartments for Rent</p>',
    },
  },
  slotGroups: {
    'hero.bg_image': featuredImageDef,
    'hero.cta_link': ctaLinkDef,
  },
};

(Hero as any).contentSchema = contentSchema;

export default Hero;
